import { useEffect, useMemo, useState } from 'react';
import ApplicationCooLayout from '../../layout/application-coo-layout';
import { TextSubheader } from '../../components/text/text-subheader';
import { P2Button } from 'react-bootstrap-common-script';
import { stageETrackingId } from '../../utils/wogaa-ids';
import { useWogaa } from '../../hooks/use-wogaa';
import { useAppDispatch, useAppSelector } from '../../redux/hook';
import moment from 'moment';
import { Separator } from '../../components/separator';
import { useNavigate } from 'react-router-dom';
import { useCheckHdbExistingRentalQuery, useGetApplicationDetailQuery, useGetCpfWithdrawalOverviewQuery } from '../../redux/services/getStageE';
import { StageEContextProvider } from '../../context/stage-e-context';
import { CpfServiceStatusEnum } from '../../models/enum/cpf-service-status.enum';
import { Parties } from '../../models/interfaces/stage-e.interface';
import StageE2View from './stage-e-2/stage-e2-view';
import StageE1View from './stage-e-1/stage-e1-view';
import { useGetStageD1ObjQuery } from '../../redux/services/getStageD';
import StageE3View from './stage-e-3/stage-e3-view';
import { LottieLoader } from '../../components/loader';
import { currentOwnerIncludedEndorseManuallySentDraft } from '../../utils/validators/stage-d/endorse-validator';
import { Helpers } from '../../utils/helper';
import { SolicitorTypeEnum } from '../../models/enum/solicitor-type.enum';
import { LoanTypeEnum } from '../../models/enum/loan-type.enum';
import { StructureStatusEnum } from '../../models/enum/structure-status.enum';
import PrivateSolicitorView from './private-solicitor-view/private-solicitor-view';
import { StageELegacyView } from './stage-e-legacy';

/** Phase 1 feature release date - only applications created after this date will see the new Stage E */
const PHASE_1_RELEASE_DATE = '2025-12-16';

const StageEPage = () => {
  /** -------------------------------
   * Imports and Utilities
   --------------------------------- */
  const navigate = useNavigate();
  const queryParameters = new URLSearchParams(window.location.search);

  /** -------------------------------
   * Query Parameters and Constants
   --------------------------------- */
  const isDebug = queryParameters.get('debug'); // TODO: Remove when done
  const application_id = queryParameters.get('application_id');

  /** -------------------------------
   * Redux Selectors
   --------------------------------- */
  const singpassToken = useAppSelector((state) => state.auth.AuthenticationSlice.singpassToken);
  const loginUser = useAppSelector((state) => state.auth.AuthenticationSlice.loginUser);

  const startApplicationFormData = useAppSelector((state) => state.app.ApplicationStartSlice.formData);
  const applicationStep1FormData = useAppSelector((state) => state.app.ApplicationStep1Slice.formData);
  const applicationStep2FormData = useAppSelector((state) => state.app.ApplicationStep2Slice.formData);
  const applicationStep3FormData = useAppSelector((state) => state.app.ApplicationStep3Slice.formData);

  /** -------------------------------
   * Local State
   --------------------------------- */
  const [showE2, setShowE2] = useState<boolean>(false);
  const [showE3, setShowE3] = useState<boolean>(false);
  const [allowNextToE2, setAllowNextToE2] = useState<boolean>(false);
  const [allowNextToE3, setAllowNextToE3] = useState<boolean>(false);
  const [postCompletionPendingItems, setPostCompletionPendingItems] = useState<string[]>([]);
  const [isPostCompletionReviewing, setIsPostCompletionReviewing] = useState(false);

  /** -------------------------------
   * API Queries
   --------------------------------- */
  const {
    data: prepareForCompletionDetails,
    isLoading: prepareForCompletionDetailsIsLoading,
    isError: prepareForCompletionDetailsIsError,
  } = useGetCpfWithdrawalOverviewQuery({
    application_id: application_id ?? '0',
    uinfin: loginUser?.uinfin ?? '',
    singpass_token: singpassToken ?? '0',
  });

  const {
    data: applicationDetail,
    isLoading: applicationDetailIsLoading,
    isError: applicationDetailIsError,
  } = useGetApplicationDetailQuery(
    {
      application_id: application_id ?? '0',
      uinfin: loginUser?.uinfin ?? '',
      singpass_token: singpassToken ?? '',
    },
    { skip: application_id === '0' || application_id === null }
  );

  // Retrieve existing HDB rental information for the Stage E2 view
  const {
    data: existingHdbRentalData,
    isLoading: existingHdbRentalDataIsLoading,
    isError: existingHdbRentalDataIsError,
  } = useCheckHdbExistingRentalQuery({
    application_id: application_id ?? '0',
    uinfin: loginUser.uinfin ?? '',
    singpass_token: singpassToken ?? '0',
    hdb_ref: startApplicationFormData?.selected_hdb_property?.hdb_ref ?? '',
  });

  // NOTE: Reusing D1 query as segments required in Post Completion(E3) is the same. (Endorse, Documents, Messages)
  const {
    data: stageD1Data,
    isLoading: stageD1IsLoading,
    refetch: stageD1DataRefetch,
    isUninitialized: stageD1IsUninitialized,
    isError: stageD1IsError,
  } = useGetStageD1ObjQuery(
    {
      application_uuid: application_id ?? '',
      uinfin: loginUser?.uinfin ?? '',
      stage: 'D1',
      singpass_token: singpassToken ?? '',
    },
    { skip: !application_id }
  );

  /** -------------------------------
   * Derived Data
   --------------------------------- */
  //  Struct Check
  const struct =
    applicationStep1FormData?.household?.owners?.find((e) => loginUser?.uinfin === e?.myinfo?.uinfin)?.structure_status ??
    applicationStep1FormData?.household?.incoming_owners?.find((e) => loginUser?.uinfin === e?.myinfo?.uinfin)?.structure_status;

  const isAllPrivate = useMemo(() => {
    const isCurrentSolicitorHDB =
      applicationStep3FormData.current_solicitor_type === SolicitorTypeEnum.hdb_solicitor ||
      applicationStep3FormData.current_solicitor_type === SolicitorTypeEnum.conveyancing_hdb_solicitor;

    const isProposedSolicitorHDB =
      applicationStep3FormData.proposed_solicitor_type === SolicitorTypeEnum.hdb_solicitor ||
      applicationStep3FormData.proposed_solicitor_type === SolicitorTypeEnum.conveyancing_hdb_solicitor;

    return (
      applicationStep2FormData.current_loan_type !== LoanTypeEnum.hdb_loan &&
      applicationStep2FormData.new_loan_type !== LoanTypeEnum.hdb_loan &&
      !isCurrentSolicitorHDB &&
      !isProposedSolicitorHDB
    );
  }, [applicationStep2FormData, applicationStep3FormData]);

  /** -------------------------------
   * Phase 1 Detection Logic
   * Phase 1 = No Loan to No Loan + Inclusion
   * Inclusion = Has retaining owner(s) AND incoming owner(s) in proposed household
   * Only show new Stage E for Phase 1 applications created after Dec 16, 2025
   --------------------------------- */
  const isPhase1Scenario = useMemo(() => {
    const isNoLoanToNoLoan =
      applicationStep2FormData.current_loan_type === LoanTypeEnum.no_loan &&
      applicationStep2FormData.new_loan_type === LoanTypeEnum.no_loan;

    // Inclusion = has retaining owner(s) AND incoming owner(s) in proposed household
    const incomingOwners = applicationStep1FormData?.household?.incoming_owners ?? [];
    const hasRetainingOwner = incomingOwners.some(
      (owner) => owner.structure_status === StructureStatusEnum.retaining
    );
    const hasIncomingOwner = incomingOwners.some(
      (owner) => owner.structure_status === StructureStatusEnum.incoming
    );
    const isInclusion = hasRetainingOwner && hasIncomingOwner;

    return isNoLoanToNoLoan && isInclusion;
  }, [applicationStep1FormData, applicationStep2FormData]);

  const isApplicationCreatedAfterPhase1Release = useMemo(() => {
    const submitDate = startApplicationFormData?.submit_date;
    if (!submitDate) return false;

    const releaseDate = moment(PHASE_1_RELEASE_DATE);
    const applicationDate = moment(submitDate);

    return applicationDate.isAfter(releaseDate);
  }, [startApplicationFormData?.submit_date]);

  /** 
   * Show new Stage E only for:
   * 1. Phase 1 scenarios (No Loan → No Loan + Inclusion)
   *    - Inclusion = retaining owner(s) + incoming owner(s) in proposed household
   * 2. Applications created after Dec 16, 2025
   * Otherwise, show the legacy simple static Stage E
   */
  const shouldShowNewStageE = isPhase1Scenario && isApplicationCreatedAfterPhase1Release;

  /** -------------------------------
   * Navigation Handlers
   --------------------------------- */
  // Back btn handler; Allow toggling of E2 to E1, and E1 to stages flow or to dashboard
  const handleNavigateBack = () => {
    if (showE3) {
      setShowE3(false);
      setShowE2(true);
    } else if (showE2) {
      setShowE2(false);
      // E1 will be shown by default when both showE2 and showE3 are false
    } else {
      navigate(`/application-coo?application_id=${application_id}`);
    }
  };

  const handleNavigateNext = () => {
    // If currently on E1 and allowed, go to E2
    if (!showE2 && !showE3 && allowNextToE2) {
      setShowE2(true);
      return;
    }
    // If currently on E2 and allowed, go to E3
    if (showE2 && !showE3 && allowNextToE3) {
      setShowE2(false);
      setShowE3(true);
      return;
    }
  };

  /** -------------------------------
   * Lifecycle Effects
   --------------------------------- */
  // WOGAA tracking for Stage E - auto start and complete (page view tracking)
  useWogaa({
    trackingId: stageETrackingId,
    autoStart: true,
    autoComplete: true,
    applicationId: application_id ?? undefined,
    hdbRef: startApplicationFormData?.selected_hdb_property?.hdb_ref,
  });

  /** -------------------------------
   * E3 - Calculation
   --------------------------------- */

  /** -------------------------
   * Check for endorsement
   --------------------------- */
  // Find login user's spouse name if needed for endorse forms
  const loginUserSpouse =
    applicationStep1FormData?.household?.incoming_owners?.find((owner) => owner.myinfo.uinfin === loginUser.uinfin)?.spouse ?? '';

  /**
   * Filter the endorse_forms data to find forms that have a status of
   * "manually_sent" and "draft," where the logged-in user is an owner and the
   * form has not been signed by all owners.
   */
  const currentOwnerIncludedEndorseManuallySent = useMemo(
    () => currentOwnerIncludedEndorseManuallySentDraft(stageD1Data?.endorse_forms, loginUser.uinfin, loginUserSpouse),
    [stageD1Data?.endorse_forms, loginUser.uinfin, loginUserSpouse]
  );

  /**
   * Extract the number of pending forms from by logged-in user
   * "currentOwnerIncludedEndorseManuallySent"
   */
  const numPendingCurrentOwnerEndorseManuallySent = currentOwnerIncludedEndorseManuallySent.filter((form) =>
    form.owners.find((owner) => owner.uinfin === loginUser.uinfin && owner.endorsement_date === undefined)
  ).length;

  /** -------------------------
   * Check for any pending CPF 
   * withdrawal requests
   --------------------------- */
  const isCpfServiceRequired = useMemo(
    () =>
      !!prepareForCompletionDetails?.applicantEServiceEnabled &&
      prepareForCompletionDetails?.parties?.some(({ withdrawalRequests }: Parties) =>
        withdrawalRequests.some(({ status }) => status === CpfServiceStatusEnum.pending)
      ),
    [prepareForCompletionDetails]
  );

  // Note - Logic modified from stage-d1 landing page, possible to refactor but prefer not to touch/modify production code.
  useEffect(() => {
    if (!stageD1Data) return;

    // Destructure arrays and properties with sensible defaults.
    // const folders = stageD1Data?.folders ?? [];
    const requestFolders = stageD1Data?.folders?.filter((item) => item?.is_request_document === true) ?? [];
    const messages = stageD1Data?.message_info ?? [];
    const messageType = stageD1Data?.message_type ?? 'GENERIC';

    if (requestFolders.length || messages.length || numPendingCurrentOwnerEndorseManuallySent) {
      const pendingItems: string[] = [];

      // Add pending items for messages.
      if (messages.length > 0) {
        pendingItems.push(...Array(messages.length).fill('messages'));
      }

      // Process folder documents.
      if (requestFolders.length > 0) {
        // Exclude folders named "Letter of Lawyer's Representation".
        const nonLawyerLetterDocs = requestFolders.filter((folder) => folder.name !== "Letter of Lawyer's Representation");

        // For GENERIC messageType or if valid folders exist, add pending document items.
        if (messageType === 'GENERIC' || nonLawyerLetterDocs.length > 0) {
          const documentsCount = messageType === 'GENERIC' ? requestFolders.length : nonLawyerLetterDocs.length;
          pendingItems.push(...Array(documentsCount).fill('documents'));
        }
      }
      if (currentOwnerIncludedEndorseManuallySent.length > 0) {
        pendingItems.push(...Array(currentOwnerIncludedEndorseManuallySent.length).fill('endorse_forms'));
      }

      console.log('Setting pending ', pendingItems);
      setPostCompletionPendingItems(pendingItems);
    } else if (currentOwnerIncludedEndorseManuallySent.length === 0) {
      setIsPostCompletionReviewing(true);
    }
  }, [stageD1Data, currentOwnerIncludedEndorseManuallySent]);

  // Determines which stage (E1,E2 or E3) to display based on application details and status
  useEffect(() => {
    if (!prepareForCompletionDetails || !applicationDetail) return;

    const currentDate = moment();
    const completionApptDate = moment(prepareForCompletionDetails.appointmentDate);
    const polymerStage = applicationDetail.polymer_stage;

    // Handle Stage E2
    if (polymerStage === 'E2') {
      if ((isCpfServiceRequired || postCompletionPendingItems.length > 0) && currentDate.isAfter(completionApptDate, 'day')) {
        // Other future conditions: Forms + Message + Doc request + CPF Rejected(Should have pending)
        setShowE3(true);
        setAllowNextToE3(true);
      } else {
        setShowE2(true);
        setAllowNextToE2(true);
      }
      return;
    }

    // Handle Stage E
    if (polymerStage === 'E') {
      // Check if the completion date has passed.
      // appointmentDate is absent until HDB fixes the actual appointment — moment(undefined)
      // would equal "now" and wrongly flip unbooked cases into E2, so guard it explicitly.
      const isCompletionDate = !!prepareForCompletionDetails.appointmentDate && currentDate.isSame(completionApptDate, 'day');
      const isAfterCompletionAppointment = Helpers.isTodayAfterGivenDate(prepareForCompletionDetails.appointmentDate ?? '');
      // Display E2 if there are no CPF withdrawal forms and the completion date has been reached; otherwise, default to E1.

      if (!isCpfServiceRequired && (isCompletionDate || isAfterCompletionAppointment)) {
        setShowE2(true);
        setAllowNextToE2(true);
      }
    }
  }, [applicationDetail, prepareForCompletionDetails, postCompletionPendingItems, isCpfServiceRequired]);

  /** -------------------------------
   * Render
   --------------------------------- */

  if (showE3) {
    // Pass in CPF required? or Pending items
    return (
      <StageE3View
        data={stageD1Data}
        dataIsUninitialized={stageD1IsUninitialized}
        pendingItems={postCompletionPendingItems}
        setPendingItems={setPostCompletionPendingItems}
        refetch={stageD1DataRefetch}
        isCpfServiceRequired={isCpfServiceRequired ?? false}
        isPostCompletionReviewing={isPostCompletionReviewing}
        setIsPostCompletionReviewing={setIsPostCompletionReviewing}
        currentOwnerIncludedEndorseManuallySent={currentOwnerIncludedEndorseManuallySent}
      />
    );
  }

  if (
    prepareForCompletionDetailsIsLoading ||
    applicationDetailIsLoading ||
    stageD1IsLoading ||
    existingHdbRentalDataIsLoading ||
    prepareForCompletionDetailsIsError ||
    applicationDetailIsError ||
    stageD1IsError ||
    existingHdbRentalDataIsError
  ) {
    return <LottieLoader />;
  }

  // Show Legacy Stage E for non-Phase 1 scenarios or applications created before Dec 16, 2025
  if (!shouldShowNewStageE) {
    return (
      <ApplicationCooLayout>
        <div className='polymer-application-coo__wrapper cpf-wrapper'>
          <TextSubheader
            subheaderMain='Stage E: Prepare For Completion'
            subheaderSub={startApplicationFormData.selected_hdb_property?.address}
            paddingLeftRight={42}
          />
          <StageELegacyView />
          <div className='cpf-wrapper_button-wrapper'>
            <div>
              <Separator />
            </div>
            <div className='cpf-wrapper_button-group'>
              <P2Button onClick={() => handleNavigateBack()} variant={'outline-primary'} style={{ fontSize: '1rem' }}>
                Back
              </P2Button>
            </div>
          </div>
        </div>
      </ApplicationCooLayout>
    );
  }

  // Default show E1 (New Stage E for Phase 1 scenarios)
  return (
    <StageEContextProvider>
      <ApplicationCooLayout>
        <div className='polymer-application-coo__wrapper cpf-wrapper'>
          <TextSubheader
            subheaderMain='Stage E: Prepare For Completion'
            subheaderSub={startApplicationFormData.selected_hdb_property?.address}
            paddingLeftRight={42}
          />
          {isAllPrivate && <PrivateSolicitorView />}
          {!isAllPrivate && !showE2 && !showE3 && <StageE1View struct={struct ?? ''} />}
          {!isAllPrivate && showE2 && (
            <StageE2View
              existingRent={existingHdbRentalData?.existing_rent}
              isProcessingE2={
                Helpers.isTodayAfterGivenDate(prepareForCompletionDetails?.appointmentDate ?? '') && applicationDetail?.polymer_stage === 'E2'
              }
            />
          )}

          <div className='cpf-wrapper_button-wrapper'>
            <div>
              <Separator />
            </div>
            <div className='cpf-wrapper_button-group'>
              <P2Button onClick={() => handleNavigateBack()} variant={'outline-primary'} style={{ fontSize: '1rem' }}>
                Back
              </P2Button>

              {/* Navigate Next Btns */}
              {!showE2 && !showE3 && allowNextToE2 && (
                <P2Button onClick={() => handleNavigateNext()} variant='primary-1'>
                  Next
                </P2Button>
              )}
              {showE2 && !showE3 && allowNextToE3 && (
                <P2Button onClick={() => handleNavigateNext()} variant='primary-1'>
                  Next
                </P2Button>
              )}
            </div>
          </div>
        </div>
      </ApplicationCooLayout>
    </StageEContextProvider>
  );
};

export default StageEPage;
